import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { useOrgStore } from '@/lib/store'

import type { ConversationDetail, Message } from './api'
import {
  archiveConversation,
  createConversation,
  deleteConversation,
  fetchAccess,
  fetchConversation,
  listConversations,
  pinConversation,
  renameConversation,
  sendMessage,
} from './api'

// Query keys are scoped by active org so cache never bleeds across organizations.
const keys = {
  access: (org: string | null) => ['copilot', 'access', org] as const,
  conversations: (org: string | null) => ['copilot', 'conversations', org] as const,
  conversation: (org: string | null, id: string) => ['copilot', 'conversation', org, id] as const,
}

export function useCopilotAccess() {
  const org = useOrgStore((s) => s.activeOrgId)
  return useQuery({
    queryKey: keys.access(org),
    queryFn: fetchAccess,
    staleTime: 10 * 60 * 1000,
  })
}

export function useConversations() {
  const org = useOrgStore((s) => s.activeOrgId)
  return useQuery({
    queryKey: keys.conversations(org),
    queryFn: listConversations,
  })
}

export function useConversation(id: string | null) {
  const org = useOrgStore((s) => s.activeOrgId)
  return useQuery({
    queryKey: keys.conversation(org, id ?? ''),
    queryFn: () => fetchConversation(id as string),
    enabled: Boolean(id),
    // While the latest turn is still being generated (assistant message is `pending`) — e.g.
    // after a browser reload mid-request — poll so the finished answer appears on its own, no
    // manual reload. Stop once it completes/errors, or if it's been pending too long (server
    // likely died) so we don't poll forever.
    refetchInterval: (query) => {
      const data = query.state.data as ConversationDetail | undefined
      const last = data?.messages[data.messages.length - 1]
      if (!last || last.role !== 'assistant' || last.status !== 'pending') return false
      const ageMs = Date.now() - new Date(last.created_at).getTime()
      return ageMs < 3 * 60_000 ? 2000 : false
    },
  })
}

export function useCreateConversation() {
  const org = useOrgStore((s) => s.activeOrgId)
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (title?: string) => createConversation(title ?? ''),
    onSuccess: () => qc.invalidateQueries({ queryKey: keys.conversations(org) }),
  })
}

export function useSendMessage() {
  const org = useOrgStore((s) => s.activeOrgId)
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ conversationId, content }: { conversationId: string; content: string }) =>
      sendMessage(conversationId, content),
    // Optimistically show the user's message immediately (before the assistant replies).
    onMutate: async ({ conversationId, content }) => {
      const key = keys.conversation(org, conversationId)
      await qc.cancelQueries({ queryKey: key })
      const prev = qc.getQueryData<ConversationDetail>(key)
      if (prev) {
        const optimistic: Message = {
          id: `optimistic-${Date.now()}`,
          role: 'user',
          content,
          sequence: (prev.messages[prev.messages.length - 1]?.sequence ?? -1) + 1,
          status: 'complete',
          meta: {},
          artifacts: [],
          tool_calls: [],
          created_at: new Date().toISOString(),
        }
        qc.setQueryData<ConversationDetail>(key, { ...prev, messages: [...prev.messages, optimistic] })
      }
      return { key, prev }
    },
    onError: (_err, _vars, ctx) => {
      if (ctx?.prev) qc.setQueryData(ctx.key, ctx.prev)
    },
    onSettled: (_data, _err, { conversationId }) => {
      qc.invalidateQueries({ queryKey: keys.conversation(org, conversationId) })
      qc.invalidateQueries({ queryKey: keys.conversations(org) })
    },
  })
}

export function useRenameConversation() {
  const org = useOrgStore((s) => s.activeOrgId)
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, title }: { id: string; title: string }) => renameConversation(id, title),
    onSuccess: (_data, { id }) => {
      qc.invalidateQueries({ queryKey: keys.conversations(org) })
      qc.invalidateQueries({ queryKey: keys.conversation(org, id) })
    },
  })
}

export function usePinConversation() {
  const org = useOrgStore((s) => s.activeOrgId)
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => pinConversation(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: keys.conversations(org) }),
  })
}

export function useArchiveConversation() {
  const org = useOrgStore((s) => s.activeOrgId)
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => archiveConversation(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: keys.conversations(org) }),
  })
}

export function useDeleteConversation() {
  const org = useOrgStore((s) => s.activeOrgId)
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => deleteConversation(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: keys.conversations(org) }),
  })
}
